import React, { useEffect, useState } from 'react';
import { PageHeader, Card, Spinner, Empty, Badge, Input } from '../../components/shared/UI';
import api from '../../utils/api';

const TYPE_COLORS = {
  feat: '#10B981',
  fix: '#EF4444',
  perf: '#8B5CF6',
  refactor: '#2E75B6',
  docs: '#64748b',
  chore: '#94a3b8',
  test: '#F59E0B',
  style: '#EC4899',
};

// "feat(admin): show trial expiry" → { type: 'feat', scope: 'admin', text: 'show trial expiry' }
function parseSubject(s) {
  const m = /^(\w+)(?:\(([^)]*)\))?!?:\s*(.*)$/.exec(s);
  if (!m || !TYPE_COLORS[m[1]]) return { type: null, scope: null, text: s };
  return { type: m[1], scope: m[2] || null, text: m[3] };
}

export default function AdminChangelog() {
  const [entries, setEntries] = useState(null);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');

  useEffect(() => {
    api.get('/admin/changelog')
      .then(r => setEntries(r.data.entries || []))
      .catch(err => setError(err.response?.data?.message || 'Could not load changelog'));
  }, []);

  if (error) return <Empty icon="⚠️" message={error} />;
  if (!entries) return <Spinner />;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? entries.filter(e =>
        `v${e.version} ${e.build}`.toLowerCase().includes(q) ||
        e.changes.some(c => c.toLowerCase().includes(q)))
    : entries;

  const fmtDate = d => new Date(d).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div>
      <PageHeader
        title="Version History"
        subtitle={entries[0] ? `Current: v${entries[0].version} (build ${entries[0].build}) · ${entries.length} releases` : `${entries.length} releases`}
      />

      <div style={{ marginBottom: 20, maxWidth: 420 }}>
        <Input placeholder="Search versions or changes…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0
        ? <Empty icon="🔍" message="No versions match your search" />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 780 }}>
            {filtered.map((e, idx) => (
              <Card key={`${e.build}-${idx}`} style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: e.changes.length ? 10 : 0 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--heading)' }}>
                    v{e.version || '?'}
                  </span>
                  <Badge label={`build ${e.build ?? '?'}`} color="#2E75B6" />
                  {idx === 0 && !q && <Badge label="current" color="#10B981" />}
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 'auto' }}>
                    {fmtDate(e.date)}
                  </span>
                </div>
                {e.changes.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--text-subtle)', fontStyle: 'italic' }}>Maintenance release (no recorded changes)</div>
                  : (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {e.changes.map((c, i) => {
                        const p = parseSubject(c);
                        return (
                          <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13, color: 'var(--text)' }}>
                            {p.type && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                                color: TYPE_COLORS[p.type], background: `${TYPE_COLORS[p.type]}18`,
                                border: `1px solid ${TYPE_COLORS[p.type]}40`, borderRadius: 10,
                                padding: '1px 7px', whiteSpace: 'nowrap', flexShrink: 0,
                              }}>
                                {p.type}{p.scope ? `·${p.scope}` : ''}
                              </span>
                            )}
                            <span>{p.text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
